import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Save, Settings } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { FormControl, InputLabel, MenuItem, Select as MuiSelect } from '@mui/material';

export default function PosSettingsPage() {
  const { t } = useTranslation();
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const branchId = parseInt(localStorage.getItem('branchId'), 10);
  
  const [loading, setLoading] = useState(false);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [selectedInvoiceTypeId, setSelectedInvoiceTypeId] = useState(null);

  const [settings, setSettings] = useState({
    companyId,
    branchId,
    invoiceTypeId: null,
    showCategories: true,
    showProducts: true,
    showOrderType: true,
    showImages: true,
    showBarcode: false
  });

  // Fetch Invoice Types
  useEffect(() => {
    if (!companyId) return;
    
    axios.get("/InvoiceTypes", { params: { companyId } })
      .then((res) => {
        const types = res.data || [];
        setInvoiceTypes(types);
        
        // Try to find a default POS type (e.g., ID 11 or name contains 'بيع' or 'POS')
        // Or just pick the first one if available
        // Usually, POS invoice type is ID 11 based on previous code.
        const defaultType = types.find(t => t.invoiceTypeId === 11) 
                         || types.find(t => t.name.includes("نقطة") || t.name.toLowerCase().includes("pos"))
                         || types.find(t => t.name.includes("بيع")); // Fallback

        if (defaultType) {
          setSelectedInvoiceTypeId(defaultType.invoiceTypeId);
        } else if (types.length > 0) {
           setSelectedInvoiceTypeId(types[0].invoiceTypeId);
        }
      })
      .catch(err => console.error("Failed to load invoice types", err));
  }, [companyId]);

  // Fetch Settings when selectedInvoiceTypeId changes
  useEffect(() => {
    if (!companyId || !branchId || !selectedInvoiceTypeId) return;
    
    setLoading(true);
    axios.get('/PosSettings/by-branch', { 
      params: { 
        companyId, 
        branchId, 
        invoiceTypeId: selectedInvoiceTypeId 
      } 
    })
      .then(res => {
        if (res.data) {
          // Merge with defaults, ensure invoiceTypeId is set
          setSettings(prev => ({ 
            ...prev, 
            ...res.data, 
            invoiceTypeId: selectedInvoiceTypeId 
          }));
        }
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [companyId, branchId, selectedInvoiceTypeId]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!selectedInvoiceTypeId) {
        toast.error("يرجى اختيار نوع الفاتورة");
        return;
    }
    setLoading(true);
    try {
      // Ensure current selected ID is in payload
      const payload = { ...settings, invoiceTypeId: selectedInvoiceTypeId };
      await axios.post('/PosSettings', payload);
      toast.success("تم حفظ إعدادات نقطة البيع بنجاح");
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || err.message || "فشل حفظ الإعدادات";
      toast.error(`فشل الحفظ: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2 border-b">
          <CardTitle className="text-xl font-bold flex items-center gap-2">
            <Settings className="text-blue-600" />
            إعدادات نقطة البيع (POS)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">

          {/* Invoice Type Selection */}
          <div className="p-4 border rounded-lg bg-gray-50">
             <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="invoice-type-select-label">نوع فاتورة نقطة البيع</InputLabel>
                <MuiSelect
                  labelId="invoice-type-select-label"
                  value={selectedInvoiceTypeId || ''}
                  onChange={(e) => setSelectedInvoiceTypeId(e.target.value)}
                  label="نوع فاتورة نقطة البيع"
                  sx={{ bgcolor: 'white' }}
                >
                  {invoiceTypes.map((type) => (
                    <MenuItem key={type.invoiceTypeId} value={type.invoiceTypeId}>
                      {type.name}
                    </MenuItem>
                  ))}
                </MuiSelect>
             </FormControl>
             <p className="text-sm text-gray-500 mt-2">حدد نوع الفاتورة الذي تريد تطبيق هذه الإعدادات عليه (عادةً: فاتورة مبيعات نقدية / نقطة بيع).</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">إظهار المجموعات</Label>
                <p className="text-sm text-gray-500">عرض قائمة المجموعات في شاشة البيع</p>
              </div>
              <Switch
                checked={settings.showCategories}
                onCheckedChange={(v) => handleChange('showCategories', v)}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">إظهار المنتجات</Label>
                <p className="text-sm text-gray-500">عرض المنتجات مباشرة</p>
              </div>
              <Switch
                checked={settings.showProducts}
                onCheckedChange={(v) => handleChange('showProducts', v)}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">إظهار نوع الطلب</Label>
                <p className="text-sm text-gray-500">سفري، محلي، توصيل...</p>
              </div>
              <Switch
                checked={settings.showOrderType}
                onCheckedChange={(v) => handleChange('showOrderType', v)}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">إظهار صور الأصناف</Label>
                <p className="text-sm text-gray-500">عرض الصور المصغرة للمنتجات</p>
              </div>
              <Switch
                checked={settings.showImages}
                onCheckedChange={(v) => handleChange('showImages', v)}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">إظهار الباركود</Label>
                <p className="text-sm text-gray-500">عرض الباركود بجانب الاسم</p>
              </div>
              <Switch
                checked={settings.showBarcode}
                onCheckedChange={(v) => handleChange('showBarcode', v)}
              />
            </div>

          </div>

          {/* Theme Settings */}
          <div className="pt-4 border-t">
            <h3 className="text-lg font-semibold mb-4">تخصيص المظهر (Theme)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              <div className="space-y-2">
                <Label>اللون الرئيسي (Primary)</Label>
                <div className="flex gap-2">
                  <input 
                    type="color" 
                    value={settings.primaryColor || '#3b82f6'} 
                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                    className="h-10 w-20 p-1 rounded cursor-pointer border"
                  />
                  <input 
                    type="text" 
                    value={settings.primaryColor || '#3b82f6'} 
                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                    className="flex-1 border rounded px-2 text-sm"
                    dir="ltr"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>لون الخلفية (Background)</Label>
                <div className="flex gap-2">
                  <input 
                    type="color" 
                    value={settings.backgroundColor || '#f3f4f6'} 
                    onChange={(e) => handleChange('backgroundColor', e.target.value)}
                    className="h-10 w-20 p-1 rounded cursor-pointer border"
                  />
                   <input 
                    type="text" 
                    value={settings.backgroundColor || '#f3f4f6'} 
                    onChange={(e) => handleChange('backgroundColor', e.target.value)}
                    className="flex-1 border rounded px-2 text-sm"
                    dir="ltr"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>لون النص (Text)</Label>
                <div className="flex gap-2">
                  <input 
                    type="color" 
                    value={settings.textColor || '#000000'} 
                    onChange={(e) => handleChange('textColor', e.target.value)}
                    className="h-10 w-20 p-1 rounded cursor-pointer border"
                  />
                  <input 
                    type="text" 
                    value={settings.textColor || '#000000'} 
                    onChange={(e) => handleChange('textColor', e.target.value)}
                    className="flex-1 border rounded px-2 text-sm"
                    dir="ltr"
                  />
                </div>
              </div>

            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button onClick={handleSave} disabled={loading} className="gap-2 bg-blue-600 hover:bg-blue-700">
              <Save />
              {loading ? "جاري الحفظ..." : "حفظ الإعدادات"}
            </Button>
          </div>

        </CardContent>
      </Card>
    </div>
  );
}
